import { useEffect, useState, useCallback, useMemo } from "react";
import { AsyncPaginate } from "react-select-async-paginate";
import { saveToState, loadFromState, resetState } from "../utils";
import { getNodes, getRiskMutual, getRiskMutualGraph } from "../api/quontxApi";
import ParquetTable from "./ParquetTable";

const ITEMS_PER_PAGE = 100;

const HEADER_NAME_MAP = {
  'n_id': 'Node Id',
  'n_label': 'Node Label',
  'ns_id': 'NodeSet Id',
  'ns_label': 'NodeSet Label',
  'exposure_uniformity_index': 'Exp. Unif.',
  'exposure_uniformity_index_ordinal': 'Exp. Unif.(lbl)',
  'w_2o_max': 'Max Weight',
  'materiality_idx': 'Materiality',
  'ti': 'Time Interval',
  'kendall_w': 'Kendall W',
  'kendall_w_ordinal': 'Kendall W',
  'icc(2,1)': 'ICC',
  'icc(2,1)_ordinal': 'ICC'
};

const COLUMNS_TO_HIDE = ['n_id', 'ns_id', 'gini_coefficient'];
const DEFAULT_SORTED_COLUMN = 'w_2o_max';

const RiskMutualFilters = () => {
  const maxEntityCount = 15;

  const [activeEntities, setActiveEntities] = useState([]);
  const [entitySelectDisabled, setEntitySelectDisabled] = useState(false);
  const [statsType, setStatsType] = useState('n_stats');
  const [graphImages, setGraphImages] = useState([]);
  const [loadingGraph, setLoadingGraph] = useState(false);

  // Key to force table reload
  const [tableKey, setTableKey] = useState(0);

  const toOption = (node, disabled) => ({
    label: node.name,
    value: node.uuid,
    isDisabled: disabled,
  });

  const manageActiveEntities = useCallback(
    (selectedEntities) => {
      const safe = selectedEntities ?? [];
      if (safe.length <= maxEntityCount) {
        setActiveEntities(safe.map((e) => ({ label: e.label, value: e.value })));
        setEntitySelectDisabled(false);
      } else {
        setEntitySelectDisabled(true);
      }
    },
    [maxEntityCount]
  );

  const resetFilters = useCallback(() => {
    resetState("riskMutualFiltersState", [
      { stateFunc: setActiveEntities, nullVal: [] },
    ]);
    setEntitySelectDisabled(false);
    setStatsType('n_stats');
    setGraphImages([]);
    setTableKey(prev => prev + 1);
  }, []);

  // Server-side node search
  const fetchNodesPage = useCallback(async ({ search, page, node_type }) => {
    const res = await getNodes({
      node_type,
      name: search,
      page,
      itemsPerPage: ITEMS_PER_PAGE,
    });
    return res?.data?.data ?? [];
  }, []);

  const loadEntityOptions = useCallback(
    async (search, _loadedOptions, additional) => {
      const q = (search || "").trim();
      if (q.length < 2) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }

      const page = additional?.page ?? 1;
      const items = await fetchNodesPage({
        search: q,
        page,
        node_type: "NAMED_ENTITY:ORGANISATION",
      });

      return {
        options: items.map((n) => toOption(n, entitySelectDisabled)),
        hasMore: items.length === ITEMS_PER_PAGE,
        additional: { page: page + 1 },
      };
    },
    [fetchNodesPage, entitySelectDisabled]
  );

  const entityAdditional = useMemo(() => ({ page: 1 }), []);

  // Fetch data function for ParquetTable
  const fetchTableData = useCallback(async () => {
    const entityUuids = activeEntities.map(e => e.value);
    return await getRiskMutual({
      entity_uuids: entityUuids,
      stats_type: statsType
    });
  }, [activeEntities, statsType]);

  // Load Graph Images
  const loadGraphImages = useCallback(async (entityUuids) => {
    try {
      setLoadingGraph(true);

      const response = await getRiskMutualGraph({
        entity_uuids: entityUuids,
        stats_type: 'ns_stats_graph'
      });

      const graphsData = response.data.graphs;
      setGraphImages(graphsData);
      setLoadingGraph(false);
    } catch (err) {
      console.error('Error loading graph images:', err);
      setLoadingGraph(false);
    }
  }, []);

  // Load graphs when NodeSet Graphs is selected
  useEffect(() => {
    const entityUuids = activeEntities.map(e => e.value);

    if (entityUuids.length < 3) {
      setGraphImages([]);
      return;
    }

    if (statsType === 'ns_stats_graph') {
      loadGraphImages(entityUuids);
    }
  }, [activeEntities, statsType, loadGraphImages]);

  // Enable/disable select based on maxEntityCount
  useEffect(() => {
    const totalSelected = activeEntities?.length ?? 0;
    if (totalSelected < maxEntityCount) {
      setEntitySelectDisabled(false);
    } else {
      setEntitySelectDisabled(true);
    }
  }, [activeEntities, maxEntityCount]);

  // Restore/persist localStorage
  useEffect(() => {
    loadFromState("riskMutualFiltersState", [
      {
        stateFunc: setActiveEntities,
        storageObjectKey: "activeEntities",
        defaultVal: [],
      },
    ]);
  }, []);

  useEffect(() => {
    if (activeEntities.length > 0) {
      saveToState("riskMutualFiltersState", {
        activeEntities,
      });
    }
  }, [activeEntities]);

  // Determine if table should load
  const shouldLoadTable = activeEntities.length >= 3 &&
                          (statsType === 'n_stats' || statsType === 'ns_stats');

  return (
    <div className="w-full flex flex-col items-center content-center">
      <div className="w-full flex justify-center">
        <div className="w-full max-w-6xl flex flex-col gap-8">
          <div className="flex flex-col gap-4 items-center justify-center mb-8">
            <div className="flex gap-4 justify-center mb-8">
              <div className="w-[360px] flex flex-col gap-2">
                <p>Organization Profiles (Select at least 3, max 5)</p>
                <AsyncPaginate
                  isMulti
                  value={activeEntities}
                  onChange={manageActiveEntities}
                  loadOptions={loadEntityOptions}
                  additional={entityAdditional}
                  debounceTimeout={250}
                  cacheOptions
                  placeholder="Type to search…"
                />
                <p className="text-sm text-gray-400">
                  Selected: {activeEntities.length} / {maxEntityCount}
                </p>
              </div>
            </div>

            <button
              onClick={resetFilters}
              className="bg-blue-500 text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200"
            >
              Reset filters
            </button>
          </div>

          {activeEntities.length < 3 && (
            <p className="text-sm opacity-70 text-center">
              Please select at least 3 organizations to view mutual risk data.
            </p>
          )}

          {activeEntities.length >= 3 && (
            <div className="flex justify-center gap-4 mb-4">
              <button
                onClick={() => setStatsType('n_stats')}
                className={`px-4 py-2 rounded font-semibold transition duration-200 ${
                  statsType === 'n_stats'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-500 text-white hover:bg-gray-600'
                }`}
              >
                Node Stats
              </button>
              <button
                onClick={() => setStatsType('ns_stats')}
                className={`px-4 py-2 rounded font-semibold transition duration-200 ${
                  statsType === 'ns_stats'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-500 text-white hover:bg-gray-600'
                }`}
              >
                NodeSet Stats
              </button>
              <button
                onClick={() => setStatsType('ns_stats_graph')}
                className={`px-4 py-2 rounded font-semibold transition duration-200 ${
                  statsType === 'ns_stats_graph'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-500 text-white hover:bg-gray-600'
                }`}
              >
                NodeSet Graphs
              </button>
            </div>
          )}

          {/* Table - Only show when NOT in graph mode */}
          {statsType !== 'ns_stats_graph' && (
            <ParquetTable
              key={`${tableKey}-${statsType}`}
              fetchData={fetchTableData}
              headerNameMap={HEADER_NAME_MAP}
              columnsToHide={COLUMNS_TO_HIDE}
              defaultSortedColumn={DEFAULT_SORTED_COLUMN}
              shouldLoad={shouldLoadTable}
            />
          )}

          {/* Graph Images - Only show when NodeSet Graphs is selected */}
          {statsType === 'ns_stats_graph' && (
            <div className="w-full flex flex-col items-center mt-8">
              {loadingGraph && <p className="text-white">Loading graphs...</p>}

              {!loadingGraph && graphImages && graphImages.length > 0 && (
                <div className="flex flex-col items-center gap-8 w-full">
                  {graphImages.map((graph, idx) => (
                    <div key={idx} className="flex flex-col items-center gap-4 w-full">
                      <img
                        src={`data:image/png;base64,${graph.image}`}
                        alt={graph.title}
                        style={{
                          maxWidth: '80%',
                          height: 'auto',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}

              {!loadingGraph && (!graphImages || graphImages.length === 0) && (
                <p className="text-sm opacity-70 text-center">
                  No graphs available for the selected organizations.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RiskMutualFilters;